<script setup lang="ts">
import { queryDealerShopServiceProductById } from '@user-center/api/common'
import { $t } from '@user-center/utils/i18n'
import { storeData } from '@user-center/utils/localService'
import { getCurrencyNum, getCurrencySymbol } from '@user-center/utils/qiankun.ts'
import { computed, reactive, ref } from 'vue'

interface ProductCalRule {
  id?: string | number
  minWeight: number
  maxWeight?: number | null
  priceType?: number | string
  price?: number | string
  childRule?: ProductCalRule[]
  interval?: string
  showText?: string
}

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  transfersMap: {
    type: Object,
    default: () => ({}),
  },
})
const emit = defineEmits(['toggleDetails'])

const state = reactive({
  infoData: {} as any,
})

const loading = ref(false)

function transferText(value: any) {
  const text = String(value || '')
  return props.transfersMap[text] || text
}

function formatCarrierList(carrierList: any) {
  return (Array.isArray(carrierList) ? carrierList : [])
    .map(item => transferText(item))
    .filter(Boolean)
    .join(',')
}

function toFlatTree(list: ProductCalRule[] = []): ProductCalRule[] {
  const result: ProductCalRule[] = []
  ;(list || []).forEach((item) => {
    result.push(item)
    if (item.childRule?.length) {
      result.push(...toFlatTree(item.childRule))
    }
  })
  return result
}

function formatBillingInterval(item: ProductCalRule) {
  const minWeight = item.minWeight ?? 0
  const maxEmpty = item.maxWeight === 0 || item.maxWeight === null || typeof item.maxWeight === 'undefined'

  if (maxEmpty) {
    return `≥${minWeight}kg`
  }

  return `[${minWeight}-${item.maxWeight}kg)`
}

function formatPriceType(item: ProductCalRule) {
  if (item.childRule?.length) {
    return '↓'
  }

  const price = item.price === null || typeof item.price === 'undefined'
    ? ''
    : String(getCurrencyNum(item.price))
  const unit = getCurrencySymbol('¥')

  switch (Number(item.priceType)) {
    case 0:
      return $t('estimation.kgPrice', { price, unit })
    case 1:
      return $t('estimation.onePrice', { price, unit })
    case 2:
      return $t('estimation.per0_1kgPrice', { price, unit })
    case 3:
      return $t('estimation.per0_5kgPrice', { price, unit })
    case 99:
      return $t('estimation.originalPriceBilling')
    default:
      return price ? `${unit}${price}` : '-'
  }
}

const billingRows = computed(() => {
  const flatRules = toFlatTree(state.infoData.productCalValue || [])
  return flatRules.map(item => ({
    ...item,
    interval: formatBillingInterval(item),
    showText: formatPriceType(item),
  }))
})

function getStoreInformation() {
  if (!process.client) {
    return null
  }

  const raw = window.localStorage.getItem(storeData.StoreInformation)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  }
  catch {
    return null
  }
}

async function getDetail() {
  emit('toggleDetails')
  loading.value = true
  try {
    const storeInformation = getStoreInformation()
    if (!storeInformation?.id || !props.item?.id) {
      return
    }

    const res: any = await queryDealerShopServiceProductById({
      shopId: storeInformation.id,
      id: props.item.id,
    })

    const data = res.data
    let productCalValue = null
    if (data?.productCal?.value) {
      try {
        productCalValue = JSON.parse(data.productCal.value)
      }
      catch (error) {
        console.warn('format productCal value error', error)
      }
    }

    state.infoData = {
      ...data,
      productCalValue,
    }
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="p-6" style="border-bottom: 1px solid var(--el-border-color);">
    <div class="flex gap-36px">
      <div class="text-center" style="flex: 0 0 200px;">
        <!-- <el-avatar size="large" /> -->
        <div class="text-16px font-700 lh-30px mt-2">
          {{ transfersMap[item.productName] ? transfersMap[item.productName] : item.productName }}
        </div>
      </div>
      <div class="flex text-center" style="flex: 0 0 320px;">
        <div style="flex: 0 0 200px;">
          <p>{{ $t('text.shippingFee') }}</p>
          <p class="text-red text-18px font-700 lh-30px">
            ￥{{ item.price }}

            <template v-if="getCurrencySymbol('¥') !== '¥'">
              <br>
              ≈  {{ getCurrencySymbol('¥') }} {{ getCurrencyNum(item.price) }}
            </template>
          </p>
        </div>
        <div style="flex: 0 0 120px;">
          <p>{{ $t('text.transitTime') }}</p>
          <p class="text-18px font-700 lh-30px">
            {{ item.timeScope || '~' }}
            <span class="text-12px align-top">{{ $t('unit.day') }}</span>
          </p>
        </div>
      </div>
      <div class="flex-1 pl-10">
        <div>
          <p>{{ $t('text.shippingMode') }}</p>
          <p class="text-14px font-700 lh-30px">
            {{ transfersMap[item.freightType] ? transfersMap[item.freightType] : item.freightType }}
          </p>
        </div>
        <div>
          <p />
          {{ $t('estimation.carrierOptional') }}
          <p class="text-14px font-700 lh-30px">
            {{ formatCarrierList(item.carrierList) || '-' }}
          </p>
        </div>
      </div>
      <div class="select-btn-wrapper text-center" style="flex: 0 0 200px;">
        <el-button
          class="base-ui-primary"
          :plain="isExpanded"
          @click="getDetail"
        >
          {{ isExpanded ? $t('actions.collapse') : $t('actions.viewDetails') }}
        </el-button>
      </div>
    </div>
    <div v-show="isExpanded">
      <el-divider />
      <div
        v-if="loading"
        v-loading="loading"
        class="w-full min-h-200px"
      />
      <template v-else>
        <div>
          <div class="text-16px lh-30px my-3 font-700">
            {{ $t('estimation.billingInfo') }}
          </div>
          <div class="billing-table">
            <div class="billing-table__header">
              <div>{{ $t('estimation.billingInterval') }}</div>
              <div>{{ $t('estimation.price') }}</div>
            </div>
            <div
              v-for="(rule, index) in billingRows"
              :key="`${rule.id || index}-${index}`"
              class="billing-table__row"
            >
              <div>{{ rule.interval }}</div>
              <div>{{ rule.showText }}</div>
            </div>
            <div v-if="!billingRows.length" class="billing-table__empty">
              {{ $t('estimation.noData') }}
            </div>
          </div>
        </div>
        <div class="mt-8">
          <div class="text-16px lh-30px my-3 font-700">
            {{ $t('estimation.routeIntroduction') }}
          </div>
          <div v-html="transferText(state.infoData.productDesc || '-')" />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-btn-wrapper {
  :deep(.el-button) {
    border-radius: 20px;
    height: 40px;
    min-width: 110px;
  }
}

.billing-table {
  overflow: hidden;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  background: #fff;
}

.billing-table__header,
.billing-table__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.billing-table__header {
  background: #fafafa;
  color: #000;
  font-size: 12px;
  font-weight: 500;
}

.billing-table__header > div,
.billing-table__row > div {
  padding: 8px 12px;
  text-align: center;
}

.billing-table__row {
  border-top: 1px solid #f2f2f2;
  color: #222;
  font-size: 12px;
}

.billing-table__empty {
  border-top: 1px solid #f2f2f2;
  color: #999;
  font-size: 13px;
  padding: 12px;
  text-align: center;
}
</style>
